import React, { useState } from 'react';
import { X, Tag, MapPin, Navigation, LocateFixed, Search, RefreshCw } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import DeliveryMap from './DeliveryMap';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutModal = ({ isOpen, onClose, onOrderPlaced }) => {
  const { items, subtotal, clearCart } = useCart();
  const { settings } = useSettings();
  const [form, setForm] = useState({ customerName: '', phone: '', couponCode: '', notes: '' });
  const [discount, setDiscount] = useState(0);
  const [couponValidated, setCouponValidated] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [orderType, setOrderType] = useState('delivery');
  const [addressInput, setAddressInput] = useState('');
  const [coords, setCoords] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteStale, setQuoteStale] = useState(false);
  const [geoStatus, setGeoStatus] = useState('idle');

  if (!isOpen) return null;

  const deliveryCharge = orderType === 'delivery' ? (quote?.deliveryCharge || 0) : 0;
  const total = Math.max(0, subtotal - discount + deliveryCharge);
  const bakeryAddress = settings.address || '';
  const mapsLink = bakeryAddress
    ? `https://maps.google.com/?q=${encodeURIComponent(bakeryAddress)}`
    : 'https://maps.google.com/';

  const fetchQuote = async (payload) => {
    setQuoteLoading(true);
    try {
      const { data } = await axios.post(`${API}/delivery/quote`, payload);
      setQuote(data);
      setQuoteStale(false);
      if (data.lat != null && data.lng != null) setCoords({ lat: data.lat, lng: data.lng });
      if (data.fallback === 'geocode_failed' || data.fallback === 'no_coordinates') {
        toast.info("Couldn't pinpoint that address — a flat ₹20 delivery charge has been applied so you can still order.");
      } else if (data.fallback === 'straight_line_estimate') {
        toast.info('Using an approximate distance to calculate your delivery charge.');
      } else if (data.fallback) {
        toast.info('Using a flat ₹20 delivery charge due to a temporary issue.');
      }
    } catch (err) {
      setQuote({ deliveryCharge: 20, distanceKm: null, fallback: 'error' });
      toast.info('Could not calculate an exact delivery charge — a flat ₹20 charge has been applied.');
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported on this device. Please enter your address manually.');
      setGeoStatus('error');
      return;
    }
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setGeoStatus('idle');
        setCoords({ lat: latitude, lng: longitude });
        fetchQuote({ lat: latitude, lng: longitude });
      },
      () => {
        setGeoStatus('error');
        toast.error('Could not access your location. Please enter your address manually or allow location permission.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleFindAddress = () => {
    if (!addressInput.trim()) { toast.error('Please type an address'); return; }
    fetchQuote({ address: addressInput.trim() });
  };

  const handlePinDrag = (lat, lng) => {
    setCoords({ lat, lng });
    setQuoteStale(true);
  };

  const handleRecalculate = () => {
    if (!coords) return;
    fetchQuote({ lat: coords.lat, lng: coords.lng });
  };

  const validateCoupon = async () => {
    if (!form.couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code: form.couponCode, subtotal,
      });
      setDiscount(data.discount);
      setCouponValidated(true);
      toast.success(`Coupon applied! You save ₹${data.discount}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid coupon');
      setDiscount(0);
      setCouponValidated(false);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerName || !form.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (orderType === 'delivery' && !quote) {
      toast.error('Please set your delivery location using GPS or address first');
      return;
    }
    setLoading(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        ...form,
        items: items.map(i => ({
          productId: i.productId,
          productName: i.productName,
          image: i.image,
          size: i.size,
          price: i.price,
          quantity: i.quantity,
        })),
        subtotal,
        discount,
        total,
        orderType,
        deliveryAddress: orderType === 'delivery' ? (quote?.address || addressInput) : '',
        deliveryLocation: orderType === 'delivery' && coords ? coords : undefined,
        deliveryDistanceKm: orderType === 'delivery' ? (quote?.distanceKm || 0) : 0,
        deliveryCharge,
        deliveryFallback: orderType === 'delivery' ? (quote?.fallback || '') : '',
      });
      clearCart();
      onOrderPlaced(order);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4" data-testid="checkout-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#ECE0DA]">
          <h2 className="font-playfair text-xl font-bold text-[#1F1410]">Place Your Order</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#FDF8F3] rounded-full transition-colors" data-testid="checkout-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Order summary */}
          <div className="bg-[#FDF8F3] rounded-xl p-4 space-y-2">
            {items.map(item => (
              <div key={item.key} className="flex justify-between text-sm">
                <span className="text-[#1F1410]">{item.productName}{item.size ? ` (${item.size})` : ''} × {item.quantity}</span>
                <span className="font-semibold text-[#7A1125]">₹{item.price * item.quantity}</span>
              </div>
            ))}
            <div className="border-t border-[#ECE0DA] pt-2 flex justify-between font-semibold">
              <span>Subtotal</span><span>₹{subtotal}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-green-600 font-semibold">
                <span>Discount</span><span>-₹{discount}</span>
              </div>
            )}
            {orderType === 'delivery' && quote && (
              <div className="flex justify-between text-[#1F1410]" data-testid="checkout-delivery-line">
                <span>Delivery Charge{quote.distanceKm ? ` (${quote.distanceKm} km)` : ''}</span><span>₹{deliveryCharge}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-[#7A1125]">
              <span>Total</span><span data-testid="checkout-total">₹{total}</span>
            </div>
          </div>

          {/* Order type toggle */}
          <div className="flex gap-2" data-testid="order-type-toggle">
            {[['delivery', 'Delivery'], ['pickup', 'Pickup']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setOrderType(val)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  orderType === val ? 'bg-[#7A1125] text-white border-[#7A1125]' : 'bg-white text-[#7A6A63] border-[#ECE0DA] hover:border-[#7A1125]'
                }`}
                data-testid={`order-type-${val}`}
              >
                {label}
              </button>
            ))}
          </div>

          {orderType === 'pickup' ? (
            <div className="bg-[#FDF8F3] border border-[#ECE0DA] rounded-xl p-3 space-y-1" data-testid="pickup-info-banner">
              <div className="flex items-center gap-2 text-[#7A1125] font-semibold text-sm">
                <MapPin size={15} />
                <span>Collect from our bakery</span>
              </div>
              {bakeryAddress && <p className="text-[#7A6A63] text-xs pl-5">{bakeryAddress}</p>}
              <a
                href={mapsLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#7A1125] underline underline-offset-2 pl-5 hover:text-[#8B1A2B]"
                data-testid="get-directions-link"
              >
                <Navigation size={11} />
                Get directions on Google Maps
              </a>
            </div>
          ) : (
            <div className="space-y-3 border border-[#ECE0DA] rounded-xl p-3" data-testid="delivery-section">
              <button
                type="button"
                onClick={handleUseMyLocation}
                disabled={geoStatus === 'locating'}
                className="w-full flex items-center justify-center gap-2 bg-[#7A1125]/10 text-[#7A1125] font-semibold py-2.5 rounded-xl text-sm hover:bg-[#7A1125]/15 transition-colors"
                data-testid="use-my-location-button"
              >
                <LocateFixed size={16} className={geoStatus === 'locating' ? 'animate-spin' : ''} />
                {geoStatus === 'locating' ? 'Locating...' : 'Use my current location'}
              </button>

              <div className="flex items-center gap-2 text-[10px] text-[#7A6A63] uppercase tracking-wide">
                <div className="flex-1 h-px bg-[#ECE0DA]" /> or enter manually <div className="flex-1 h-px bg-[#ECE0DA]" />
              </div>

              <div className="flex gap-2">
                <input
                  value={addressInput}
                  onChange={e => setAddressInput(e.target.value)}
                  placeholder="Type your delivery address"
                  className="flex-1 px-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                  data-testid="manual-address-input"
                />
                <button
                  type="button"
                  onClick={handleFindAddress}
                  disabled={quoteLoading}
                  className="px-3 py-2.5 bg-[#7A1125] text-white rounded-xl text-sm font-semibold hover:bg-[#8B1A2B] transition-colors"
                  data-testid="find-address-button"
                >
                  <Search size={14} />
                </button>
              </div>

              {coords && (
                <>
                  <DeliveryMap lat={coords.lat} lng={coords.lng} onChange={handlePinDrag} />
                  {quoteStale && (
                    <button
                      type="button"
                      onClick={handleRecalculate}
                      disabled={quoteLoading}
                      className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-[#7A1125] py-2 border border-[#7A1125]/30 rounded-lg hover:bg-[#7A1125]/5 transition-colors"
                      data-testid="recalculate-button"
                    >
                      <RefreshCw size={12} /> Recalculate charge for this pin
                    </button>
                  )}
                </>
              )}

              {quoteLoading && <p className="text-xs text-[#7A6A63]" data-testid="quote-loading">Calculating delivery charge...</p>}

              {quote && !quoteStale && (
                <div className="bg-[#FDF8F3] rounded-lg p-2.5 text-xs text-[#1F1410]" data-testid="delivery-quote-result">
                  {quote.distanceKm != null ? (
                    <p>Distance: <strong>{quote.distanceKm} km</strong> · Delivery Charge: <strong>₹{quote.deliveryCharge}</strong></p>
                  ) : (
                    <p>Flat delivery charge applied: <strong>₹{quote.deliveryCharge}</strong></p>
                  )}
                  {quote.address && <p className="text-[#7A6A63] mt-0.5 truncate">{quote.address}</p>}
                </div>
              )}
            </div>
          )}

          {/* Coupon */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
              <input
                value={form.couponCode}
                onChange={e => { setForm(f => ({ ...f, couponCode: e.target.value })); setCouponValidated(false); setDiscount(0); }}
                placeholder="Coupon code (optional)"
                className="w-full pl-9 pr-3 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] focus:ring-1 focus:ring-[#7A1125]/20"
                data-testid="coupon-input"
              />
            </div>
            <button
              type="button"
              onClick={validateCoupon}
              disabled={couponLoading || couponValidated}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${couponValidated ? 'bg-green-100 text-green-700' : 'bg-[#7A1125] text-white hover:bg-[#8B1A2B]'}`}
              data-testid="apply-coupon-button"
            >
              {couponLoading ? '...' : couponValidated ? 'Applied' : 'Apply'}
            </button>
          </div>

          {/* Customer details */}
          {[
            { key: 'customerName', label: 'Your Name *', placeholder: 'Enter your name', type: 'text' },
            { key: 'phone', label: 'Phone Number *', placeholder: '+91 XXXXXXXXXX', type: 'tel' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">{f.label}</label>
              <input
                type={f.type}
                value={form[f.key]}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                required
                className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] focus:ring-1 focus:ring-[#7A1125]/20"
                data-testid={`checkout-${f.key}`}
              />
            </div>
          ))}

          <div className="flex flex-col gap-2 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold py-3 rounded-full transition-colors"
              data-testid="place-order-button"
            >
              {loading ? 'Placing Order...' : 'Place Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutModal;
